import React from "react";
import {EyeOutlined, WarningOutlined} from "@ant-design/icons";
import {Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import SectionCard from "../../common/sectionCard/SectionCard";
import {FRAUD_SIGNAL_META, REJECTION_REASON_LABELS} from "../uploads-simulator.constants";
import pillStyles from "../styles/pills.module.scss";
import styles from "../styles/results.module.scss";

/**
 * Unique count of signals that are silent in production. The report's
 * boolean flags (iterative_resubmission etc.) are derived server-side from
 * the same set as `all_fraud_signals`, so they must be unioned, not added —
 * and non-silent signals (self_authored_document causes a rejection the
 * member DOES see) don't belong under a "silent" count.
 */
function countSilent(silentSignals) {
    const signals = new Set(Array.isArray(silentSignals?.all_fraud_signals) ? silentSignals.all_fraud_signals : []);
    if (silentSignals?.accepted_after_repeated_rejections) signals.add("accepted_after_repeated_rejections");
    if (silentSignals?.iterative_resubmission) signals.add("iterative_resubmission");
    if (silentSignals?.fi_owned_routing_match) signals.add("fi_owned_routing_match");
    return [...signals].filter(signal => FRAUD_SIGNAL_META[signal]?.silentInProduction !== false).length;
}

/**
 * Side-by-side of what the member is told (the redacted `member_visible`
 * response) versus the internal verdict. A warning strip appears whenever
 * the two diverge — a rejection with a softened public reason, or an
 * acceptance whose fraud signals the member never learns about.
 *
 * @param {Object} props
 * @param {Object} [props.memberVisible] — report `member_visible`
 * @param {Object} [props.result] — report `result`
 * @param {Object} [props.silentSignals] — report `silent_signals`
 */
function MemberVsInternalCard({memberVisible, result, silentSignals}) {
    const summary = memberVisible?.verdict_summary || null;
    const memberAccepted = summary?.accepted === true;
    const internalReason = result?.reason || null;
    const silentCount = countSilent(silentSignals);
    const diverges = silentCount > 0 || (internalReason && summary?.reason && summary.reason !== internalReason);

    return (
        <SectionCard icon={<EyeOutlined />} title="Member View vs Internal Verdict">
            <div className={styles.compareGrid}>
                <div className={`${styles.comparePane} ${styles.compareMember}`}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.compareTitle}>
                        What the member sees
                    </Text>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={`${pillStyles.tag} ${memberAccepted ? pillStyles.tone_success : pillStyles.tone_danger}`}>
                        {summary ? (memberAccepted ? "Accepted" : "Rejected") : "—"}
                    </Text>
                    {memberAccepted ? (
                        <>
                            {summary?.document_id ? (
                                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.compareLine}>
                                    Document id: {summary.document_id}
                                </Text>
                            ) : null}
                            {summary?.retention ? (
                                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.compareLine}>
                                    Retention: {String(summary.retention)}
                                </Text>
                            ) : null}
                            {summary?.compensation_type ? (
                                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.compareLine}>
                                    Compensation: {summary.compensation_type}
                                </Text>
                            ) : null}
                        </>
                    ) : (
                        <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.compareLine}>
                            {summary?.reason || "—"}
                        </Text>
                    )}
                    <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.compareMeta}>
                        Job status: {memberVisible?.job_status || "—"}
                    </Text>
                </div>

                <div className={`${styles.comparePane} ${styles.compareInternal}`}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.compareTitle}>
                        Internal verdict
                    </Text>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={`${pillStyles.tag} ${result?.accepted ? pillStyles.tone_success : pillStyles.tone_danger}`}>
                        {result ? (result.accepted ? "Accepted" : "Rejected") : "—"}
                    </Text>
                    {internalReason ? (
                        <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.compareLine}>
                            {REJECTION_REASON_LABELS[internalReason] || internalReason}
                            <span className={styles.compareCode}> · {internalReason}</span>
                        </Text>
                    ) : (
                        <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.compareLine}>
                            No internal rejection reason.
                        </Text>
                    )}
                    <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.compareMeta}>
                        Silent signals: {silentCount}
                    </Text>
                </div>
            </div>

            {diverges ? (
                <div className={styles.divergenceStrip}>
                    <WarningOutlined className={styles.divergenceIcon} />
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.divergenceText}>
                        The member-visible response diverges from the internal analysis — production redacts everything on the right.
                    </Text>
                </div>
            ) : null}
        </SectionCard>
    );
}

export default MemberVsInternalCard;
